import _ from 'underscore';
import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Image } from '@fluentui/react';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getStepStyles } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { cali18n } from '../../shared/contants';
import { LearnMore } from '../../../../conversion-wizard-components/src/learn-more/learn-more';
import extIconImage from '../assets/extPic.png';
import { PmaxAutoSwitchMessage } from './pmax-auto-switch-message';

const FinishViewUnstyled = (props) => {
  const {
    classes,
    i18n,
    serverContext,
    selectedGoalType,
    gtmContext,
    EnhancedConversions,
    showPmaxAutoSwitchMessage,
  } = props;
  const {
    workflowCompleted: isGtmDone,
    publishedVersionId,
    GTMAccountId: selectedAccount,
    GTMContainerId: selectedContainer,
  } = gtmContext;
  const { AdvertiserCustomerID: cid, Id: aid } = serverContext.CurrentAccount;
  const { DynamicPermissions } = serverContext;
  const enableConvValueRules = useMemo(() => DynamicPermissions && DynamicPermissions.ConvValueRules, [DynamicPermissions]);
  const edgeExtURL = 'https://microsoftedge.microsoft.com/addons/detail/uet-tag-helper-by-micros/nmcihgehpmokipeigjpeckphhdejjpbf';
  const chromeExtURL = 'https://chrome.google.com/webstore/detail/uet-tag-helper-by-bing-ad/naijndjklgmffmpembnkfbcjbognokbf';
  const instructionTemplate = i18n.getString(_TL_('It can take up to 24 hours for us to verify your UET tags. In the meantime, you can check your UET tag status and test whether your conversion goals are set up correctly using the UET Tag Helper. You can install this browser extension on {{m}}Microsoft Edge{{/m}} or {{g}}Google Chrome{{/g}}.'));
  const cvrlInstructionTemplate = useMemo(() => i18n.getString(_TL_('It can take up to 24 hours for us to verify your UET tags. In the meantime, you can check your UET tag status and test whether your conversion goals are set up correctly using the UET Tag Helper. You can install this browser extension on {{m}}Microsoft Edge{{/m}} or {{g}}Google Chrome{{/g}}. If you need to differentiate your conversion values to reflect on your bid strategy and reflect it on your reporting, set up {{r}}Conversion value rules{{/r}}.')), [i18n]);
  const helpLabel = i18n.getString(_TL_('It can take up to 24 hours for us to verify your UET tags. In the meantime, you can check your UET tag status and test whether your conversion goals are set up correctly using the UET Tag Helper. You can install this browser extension on Microsoft Edge or Google Chrome. Learn more.'));
  const trackingInstructionTemplate = i18n.getString(_TL_('We can use this UET tag to track both conversions and audiences based on actions people take on your website. Please {{c}}Create a conversion goal{{/c}} or {{a}}Create audience{{/a}} to get started.'));
  const conversionGoalsPageLink = document.URL.replace(/vnext.*$/, `vnext/conversiongoals?cid=${cid}&aid=${aid}`);
  const audiencesLibraryPageLink = document.URL.replace(/vnext.*$/, `vnext/audienceslibrary?cid=${cid}&aid=${aid}`);
  const conversionValueRulePageLink = useMemo(() => document.URL.replace(/vnext.*$/, `vnext/conversionvaluerules?cid=${cid}&aid=${aid}`), [aid, cid]);

  const noInstructionGoals = ['ApplicationInstallGoal', 'InStoreTransactionGoal', 'InStoreVisitGoal'];

  const tagManagerLink = `https://tagmanager.google.com/#/versions/accounts/${selectedAccount}/containers/${selectedContainer}/versions/${publishedVersionId}`;

  const bulletPointTwo = useMemo(() => {
    if (EnhancedConversions) {
      return (
        <ReactTemplate
          model={{
            b: text => (<b>{text}</b>),
          }}
          template={i18n.getString(_TL_('Download the import template from {{b}}Tools > Offline Conversions > Upload{{/b}} and make sure column names and your offline conversions data are formatted correctly.'))}
        />
      );
    }
    return i18n.getString(_TL_('Download the import template and make sure column names and your offline conversions data are formatted correctly.'));
  }, [EnhancedConversions, i18n]);

  const bulletPointThree = useMemo(() => {
    if (EnhancedConversions) {
      return (
        <Fragment>
          {i18n.getString(_TL_('If you’re not using enhanced conversions, your servers might redirect ads clicks. If they do, verify that the Microsoft Click ID (MSCLKID) URL parameter is passed to your landing page.'))}
          <LearnMore i18n={i18n} topicId="ext56852" ariaLabel={i18n.getString(_TL_('How to track offline conversions'))} />
        </Fragment>
      );
    }
    return (
      <Fragment>
        {i18n.getString(_TL_('If your servers redirect ads clicks, verify that the Microsoft Click ID (MSCLKID) URL parameter is passed to your landing page.'))}
        <LearnMore i18n={i18n} topicId="ext56852" ariaLabel={i18n.getString(_TL_('How to track offline conversions'))} />
      </Fragment>
    );
  }, [EnhancedConversions, i18n]);

  return (
    <div>
      {selectedGoalType && noInstructionGoals.indexOf(selectedGoalType) > -1 && (
        <div className={classes.contentBlock}>
          <p className={classes.bodyText}>{`${i18n.getString(_TL_('You\'ve set up your'))} ${i18n.getString(cali18n(selectedGoalType))}.`}</p>
        </div>
      )}

      {selectedGoalType === 'OfflineConversionGoal' && (
        <div className={classes.contentBlock}>
          <p className={classes.bodyText}>{i18n.getString(_TL_('For offline conversions to work, you\'ll need to complete the following steps before you import your conversions.'))}</p>
          <ul>
            <li>{i18n.getString(_TL_('Capture the click ID in the URL that customers click from one of your ads.'))}</li>
            <li>{bulletPointTwo}</li>
            <li>{bulletPointThree}</li>
            {EnhancedConversions &&
              <li>{
                i18n.getString(_TL_('If you’re using enhanced conversions, follow the required hashing rules for the email address and/or phone number. In the last two columns column, add the hashed versions of your customers’ email address or phone number (or both) received from your website lead forms or at the time of conversion.'))}
                <LearnMore label={i18n.getString(_TL_('Learn more about hashing'))} i18n={i18n} topicId="app54830" ariaLabel={i18n.getString(_TL_('Learn more about hashing'))} />
              </li>
            }
          </ul>
        </div>
      )}

      {!isGtmDone && noInstructionGoals.indexOf(selectedGoalType) < 0 && selectedGoalType !== 'OfflineConversionGoal' && (
        <div className={classes.contentBlock}>
          {selectedGoalType &&
            <p className={classes.bodyText}>
              {enableConvValueRules ? i18n.getString(_TL_('Test your conversion goal next:')) : i18n.getString(_TL_('Next step to test your conversion goal:'))}
            </p>
          }
          {!selectedGoalType &&
            <p className={classes.bodyText}>
              <ReactTemplate
                model={{
                  c: text => (<a href={conversionGoalsPageLink}>{text}</a>),
                  a: text => (<a href={audiencesLibraryPageLink}>{text}</a>),
                }}
                template={trackingInstructionTemplate}
              />
            </p>
          }
          <p className={classes.bodyText}>
            <ReactTemplate
              model={{
                m: text => (<a href={edgeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
                g: text => (<a href={chromeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
                r: text => (<a href={conversionValueRulePageLink} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
              }}
              template={enableConvValueRules ? cvrlInstructionTemplate : instructionTemplate}
            />
            <LearnMore i18n={i18n} topicId="app54548" ariaLabel={helpLabel} />
          </p>
          <Image src={extIconImage} alt={i18n.getString(_TL_('Extension icon'))} />
        </div>
      )}
      {isGtmDone &&
        <div className={classes.contentBlock}>
          {publishedVersionId ? (
            <p className={classes.bodyText}>
              {i18n.getString(_TL_('We have set up your UET tag on your Google Tag Manager account. You can see the tags created in your Google Tag Manager container on the latest published version. You can also use this link to see the changes:'))}
              {' '}<a target="_blank" rel="noopener noreferrer" href={tagManagerLink}>{tagManagerLink}</a>
            </p>
          ) : (
            <p className={classes.bodyText}>
              {i18n.getString(_TL_('We are setting up your UET tag on your Google Tag Manager account. You will see the tags created in your Google Tag Manager container on the latest published version. This may take a few minutes.'))}
            </p>
          )}
          {!selectedGoalType &&
            <p className={classes.bodyText}>
              <ReactTemplate
                model={{
                  c: text => (<a href={conversionGoalsPageLink}>{text}</a>),
                  a: text => (<a href={audiencesLibraryPageLink}>{text}</a>),
                }}
                template={trackingInstructionTemplate}
              />
            </p>
          }
          <p className={classes.bodyText}>
            <ReactTemplate
              model={{
                m: text => (<a href={edgeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
                g: text => (<a href={chromeExtURL} aria-label={text} target="_blank" rel="noopener noreferrer">{text}</a>),
              }}
              template={instructionTemplate}
            />
            <LearnMore i18n={i18n} topicId="app54548" ariaLabel={helpLabel} />
          </p>
          <Image src={extIconImage} alt={i18n.getString(_TL_('Extension icon'))} />

        </div>
      }
      {showPmaxAutoSwitchMessage && <PmaxAutoSwitchMessage i18n={i18n} />}
    </div>
  );
};

FinishViewUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(getStepStyles(), _.constant(PropTypes.string.isRequired)))
    .isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  serverContext: PropTypes.objectOf(PropTypes.any).isRequired,
  selectedGoalType: PropTypes.string.isRequired,
  gtmContext: PropTypes.shape({
    workflowCompleted: PropTypes.bool.isRequired,
    publishedVersionId: PropTypes.number,
    GTMAccountId: PropTypes.number,
    GTMContainerId: PropTypes.number,
  }).isRequired,
  EnhancedConversions: PropTypes.bool,
  showPmaxAutoSwitchMessage: PropTypes.bool,
};

FinishViewUnstyled.defaultProps = {
  EnhancedConversions: false,
  showPmaxAutoSwitchMessage: false,
};

export const FinishView = withDefaultStyles(FinishViewUnstyled, getStepStyles);
